<template>
  <b-skeleton
    v-if="loading"
    animation="wave"
    height="700px"
  />
  <div v-else>
    <b-card
      class="account-card mb-2"
    >
      <b-form-group :label="$tk('ClientPortal.Account.Create.Picture')">
        <b-row>
          <b-col
            sm="6"
          >
            <b-skeleton-img
              v-if="imageUploading"
              height="120px"
              width="120px"
              no-aspect
              variant=" d-inline-block rounded align-top mr-1"
            />
            <b-avatar
              v-if="!imageUploading"
              ref="userAvatar"
              class="border align-top mr-1"
              variant="light"
              size="120px"
              rounded
              :src="model.imageUrl"
            />
            <div class="account-picture-content d-inline-block">
              <label for="file-upload">
                <input
                  id="file-upload"
                  ref="file"
                  type="file"
                  accept="image/*"
                  class="form-control form-control-sm d-none"
                  @change="handleFileUpload()"
                >
                <b-button
                  class="mb-1"
                  size="sm"
                  variant="outline-primary"
                  @click="openFileUpload"
                >
                  <GjIcon
                    name="Upload"
                    class="mr-50"
                  />
                  {{ $tk('ClientPortal.Account.Upload') }}
                </b-button>
              </label>
              <small class="d-block">
                <!-- You can upload a JPEG or PNG file (at least 200 x 200 pixels in size). Square images are best. -->
                {{ $tk('ClientPortal.Account.Upload.ImageInfo') }}
              </small>
            </div>
          </b-col>
        </b-row>
      </b-form-group>
      <b-row>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Create.AccountId')">
            <b-form-input
              type="text"
              :placeholder="$tk('ClientPortal.Account.Form.AccountIdPlaceholder')"
              :value="model.accountId"
              disabled
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group
            :label="$tk('ClientPortal.Account.Create.Name') + ' *'"
            :invalid-feedback="errors['Name']"
          >
            <b-form-input
              v-model="model.name"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Form.NamePlaceholder')"
              :state="errors['Name'] == null ? null : false"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group
            :label="$tk('ClientPortal.Account.Create.Email')"
            :invalid-feedback="errors['Email']"
          >
            <b-form-input
              v-model="model.email"
              :placeholder="$tk('ClientPortal.Account.Form.EmailPlaceholder')"
              type="email"
              :state="errors['Email'] == null ? null : false"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Create.Address')">
            <b-form-input
              v-model="model.address"
              :placeholder="$tk('ClientPortal.Account.Form.AddressPlaceholder')"
              type="text"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group
            label="Industry"
          >
            <b-form-select
              v-model="model.industry"
              :options="industryOptions"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Create.FiscalNumber')">
            <b-form-input
              v-model="model.fiscalNumber"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Form.FiscalNumberPlaceholder')"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Create.BankAccountNumber')">
            <b-form-input
              v-model="model.bankAccountNumber"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Form.BankAccountNumberPlaceholder')"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Create.PhoneNumber')">
            <b-form-input
              v-model="model.phoneNumber"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Form.PhoneNumberPlaceholder')"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <b-card class="account-card mb-2">
      <h5 class="mb-1">
        {{ $tk('ClientPortal.Account.Advertiser.Title') || 'Advertiser info (About this ad)' }}
      </h5>
      <small class="d-block mb-1 text-muted">
        {{ $tk('ClientPortal.Account.Advertiser.Info') || 'Shown to users on the "About this ad" page opened from the info icon on your served ads.' }}
      </small>
      <b-form-group :label="$tk('ClientPortal.Account.Advertiser.Logo') || 'Advertiser logo'">
        <b-row>
          <b-col sm="6">
            <b-skeleton-img
              v-if="advertiserLogoUploading"
              height="120px"
              width="120px"
              no-aspect
              variant=" d-inline-block rounded align-top mr-1"
            />
            <b-avatar
              v-if="!advertiserLogoUploading"
              class="border align-top mr-1"
              variant="light"
              size="120px"
              rounded
              :src="model.advertiserLogoUrl"
            />
            <div class="account-picture-content d-inline-block">
              <label for="advertiser-logo-upload">
                <input
                  id="advertiser-logo-upload"
                  ref="advertiserLogoFile"
                  type="file"
                  accept="image/*"
                  class="form-control form-control-sm d-none"
                  @change="handleAdvertiserLogoUpload()"
                >
                <b-button
                  class="mb-1"
                  size="sm"
                  variant="outline-primary"
                  @click="openAdvertiserLogoUpload"
                >
                  <GjIcon
                    name="Upload"
                    class="mr-50"
                  />
                  {{ $tk('ClientPortal.Account.Upload') }}
                </b-button>
              </label>
            </div>
          </b-col>
        </b-row>
      </b-form-group>
      <b-row>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Advertiser.Name') || 'Advertiser name'">
            <b-form-input
              v-model="model.advertiserName"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Advertiser.NamePlaceholder') || 'Displayed advertiser / legal name'"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Advertiser.PaidForBy') || 'Paid for by'">
            <b-form-input
              v-model="model.paidForBy"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Advertiser.PaidForByPlaceholder') || 'e.g. INITIATIVE MEDIA SA'"
            />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$tk('ClientPortal.Account.Advertiser.Location') || 'Location'">
            <b-form-input
              v-model="model.advertiserLocation"
              type="text"
              :placeholder="$tk('ClientPortal.Account.Advertiser.LocationPlaceholder') || 'e.g. Brazil'"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <div
      v-if="accountAccessManager.canManageAccount()"
      class="border-top text-right py-1"
    >
      <b-button
        class="bg-white"
        variant="outline-dark"
        size="sm"
        :to="`/${$route.params.accountId}/`"
      >
        {{ $tk('ClientPortal.Form.Actions.Cancel') }}
      </b-button>
      <b-button
        class="bg-white ml-50"
        variant="primary"
        size="sm"
        @click="save(model)"
      >
        {{ $tk('ClientPortal.Form.Actions.Save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import AccountsService from '@/api/client/accounts.service'
import { mapGetters } from 'vuex'
import UploadService from '@/api/client/upload.service'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'
import IndustryItemsService from '@/api/admin/industries.service'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      model: {},
      errors: {},
      loading: true,
      saving: false,
      imageUploading: false,
      advertiserLogoUploading: false,
      accountAccessManager: AccountAccessManager,
      industryOptions: [],
    }
  },
  computed: {
    ...mapGetters('verticalMenu', ['accountContext']),
  },
  watch: {
    '$route.params.accountId': 'reloadAccountData',
  },
  async mounted() {
    this.loading = true
    try {
      await this.fetchIndustries()
      const response = await AccountsService.getAccount(this.$route.params.accountId)
      this.model = response
    } catch (error) {
      this.error(error.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    async fetchIndustries() {
      const allIndustries = await IndustryItemsService.getAdminIndustries()

      const parentIndustries = allIndustries.filter(industry => industry.parentId === 0 || industry.parentId === null)

      this.industryOptions = parentIndustries.map(parent => {
        const children = allIndustries.filter(industry => industry.parentId === parent.id)

        return {
          label: parent.name,
          options: children.map(child => ({
            value: child.id,
            text: child.name,
          })),
        }
      })
    },
    async reloadAccountData() {
      this.loading = true
      try {
        const response = await AccountsService.getAccount(this.$route.params.accountId)
        this.model = response
      } catch (error) {
        this.error(error.message)
      } finally {
        this.loading = false
      }
    },
    async save(model) {
      this.errors = {}
      try {
        const response = await AccountsService.updateAccount(model, this.$route.params.accountId)
        if (response.created) {
          this.success(this.$tk('ClientPortal.Account.AccountUpdatedSuccessfully'))
          this.accountContext.imageUrl = this.model.imageUrl
          this.accountContext.name = this.model.name
        } else {
          this.errors = response.errors
          this.error(this.$tk('ClientPortal.Account.Error.ErrorSavingData'))
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async handleFileUpload() {
      this.imageUploading = true

      const file = this.$refs.file.files[0]
      if (file !== undefined) {
        const formData = new FormData()
        formData.append('file', file)

        try {
          const uploadResponse = await UploadService.uploadImage(formData, this.$route.params.accountId)
          if (uploadResponse.inserted) {
            this.model.imageUrl = uploadResponse.imageLink
          } else {
            this.error(uploadResponse.message)
          }
        } catch (error) {
          this.error(error.message)
        }
      }

      this.imageUploading = false
    },
    openFileUpload() {
      this.$refs.file.click()
    },
    async handleAdvertiserLogoUpload() {
      this.advertiserLogoUploading = true

      const file = this.$refs.advertiserLogoFile.files[0]
      if (file !== undefined) {
        const formData = new FormData()
        formData.append('file', file)

        try {
          const uploadResponse = await UploadService.uploadImage(formData, this.$route.params.accountId)
          if (uploadResponse.inserted) {
            this.$set(this.model, 'advertiserLogoUrl', uploadResponse.imageLink)
          } else {
            this.error(uploadResponse.message)
          }
        } catch (error) {
          this.error(error.message)
        }
      }

      this.advertiserLogoUploading = false
    },
    openAdvertiserLogoUpload() {
      this.$refs.advertiserLogoFile.click()
    },
  },
}
</script>

<!-- Create new scss file if you add more than two classes inside component -->
<style lang="scss" scoped>
.account {
  &-card {
    height: calc(100vh - 254px) ;
    padding: 16px;
    overflow-y: auto;
  }
  &-picture-content {
    width: calc(100% - 136px);
  }
}
</style>
